import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
// combineReducers is re-exported from @reduxjs/toolkit via redux
import { combineReducers } from 'redux';
import authReducer from './slices/authSlice';
import studentReducer from './slices/studentSlice';
import testReducer from './slices/testSlice';
import testSessionsReducer from './slices/testSessionsSlice';
import testSessionProgressReducer from './slices/testSessionProgressSlice';
import sessionReducer from './slices/sessionSlice';
import { authMiddleware } from './middleware/authMiddleware';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'students', 'testSessionProgress', 'tests', 'session'] // Add 'session'
};

const rootReducer = combineReducers({
  auth: authReducer,
  students: studentReducer,
  tests: testReducer,
  testSessions: testSessionsReducer,
  testSessionProgress: testSessionProgressReducer,
  session: sessionReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(authMiddleware),
});

export const persistor = persistStore(store);

// The axios interceptors (utils/axios, axios/index) attach the bearer token from
// localStorage['token'], but the token actually lives in the persisted Redux auth
// slice (under 'persist:root'). Without this bridge no Authorization header is ever
// sent — which the learning-service auth filter now rejects with 401. Mirror the
// auth token into localStorage['token'] on every change: covers login, rehydration
// after a page reload, and logout (token cleared -> key removed).
let lastSyncedToken: string | null | undefined;
const syncTokenToLocalStorage = () => {
  const token = store.getState().auth?.token ?? null;
  if (token === lastSyncedToken) return;
  lastSyncedToken = token;
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};
store.subscribe(syncTokenToLocalStorage);
syncTokenToLocalStorage();

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;